import csTools from "cornerstone-tools";
import { ARRAY_NRRD_TYPES } from "../../data";

const external = csTools.external;
const segModule = csTools.getModule("segmentation");
const getLogger = csTools.importInternal("util/getLogger");
const state = segModule.state;
const { getters } = segModule;
const logger = getLogger("store:modules:segmentationModule:getLabelmap2D");

/**
 * Returns the active `labelmap3D` and the `currentImageIdIndex`. If a labelmap does
 * not get exist, creates a new one. Generates a `labelmap2D` for the `currentImageIndex`
 * if it does not yet exist.
 *
 * @param  {HTMLElement|string} elementOrEnabledElementUID   The cornerstone enabled
 *                                                    element or its UUID.
 * @returns {Object} The `Labelmap2D`, `Labelmap3D`, `activeLabelmapIndex` and `currentImageIdIndex`.
 */
function getLabelmap2D(elementOrEnabledElementUID) {
  const element = _getElement(elementOrEnabledElementUID);

  if (!element) {
    return;
  }

  const cornerstone = external.cornerstone;
  const stackState = csTools.getToolState(element, "stack");

  if (!stackState) {
    logger.error(
      "Consumers must define stacks in their application if using segmentations in cornerstoneTools."
    );

    return;
  }

  const stackData = stackState.data[0];

  const enabledElement = cornerstone.getEnabledElement(element);

  const currentImageIdIndex = stackData.currentImageIdIndex;
  const { rows, columns } = enabledElement.image;

  const numberOfFrames = stackData.imageIds.length;
  const firstImageId = stackData.imageIds[0];

  let brushStackState = state.series[firstImageId];

  let activeLabelmapIndex;

  if (brushStackState) {
    activeLabelmapIndex = brushStackState.activeLabelmapIndex;

    if (!brushStackState.labelmaps3D[activeLabelmapIndex]) {
      const size = rows * columns * numberOfFrames;

      addLabelmap3D(brushStackState, activeLabelmapIndex, size);
    }

    if (
      !brushStackState.labelmaps3D[activeLabelmapIndex].labelmaps2D[
        currentImageIdIndex
      ]
    ) {
      addLabelmap2D(
        brushStackState,
        activeLabelmapIndex,
        currentImageIdIndex,
        rows,
        columns,
        elementOrEnabledElementUID
      );
    }
  } else {
    activeLabelmapIndex = 0;

    state.series[firstImageId] = {
      activeLabelmapIndex,
      labelmaps3D: [],
    };

    brushStackState = state.series[firstImageId];

    const size = rows * columns * numberOfFrames;

    addLabelmap3D(brushStackState, activeLabelmapIndex, size);

    addLabelmap2D(
      brushStackState,
      activeLabelmapIndex,
      currentImageIdIndex,
      rows,
      columns
    );
  }

  const labelmap3D = brushStackState.labelmaps3D[activeLabelmapIndex];

  return {
    labelmap2D: labelmap3D.labelmaps2D[currentImageIdIndex],
    labelmap3D,
    currentImageIdIndex,
    activeLabelmapIndex,
  };
}

/**
 * AddLabelmap3D - Adds a `Labelmap3D` object to the `BrushStackState` object.
 *
 * @param  {BrushStackState} brushStackState The labelmap state for a particular stack.
 * @param  {number} labelmapIndex   The labelmapIndex to set.
 * @param  {number} size            The size of the ArrayBuffer in bytes/ 2.
 * @returns {null}
 */
function addLabelmap3D(brushStackState, labelmapIndex, size) {
  const { configuration } = segModule;
  let bytesPerVoxel;

  switch (configuration.arrayType) {
    case ARRAY_NRRD_TYPES["Int8Array"]:
    case ARRAY_NRRD_TYPES["Uint8Array"]:
      bytesPerVoxel = 1;
      break;

    case ARRAY_NRRD_TYPES["Int16Array"]:
    case ARRAY_NRRD_TYPES["Uint16Array"]:
      bytesPerVoxel = 2;
      break;

    case ARRAY_NRRD_TYPES["Int32Array"]:
    case ARRAY_NRRD_TYPES["Float32Array"]:
      bytesPerVoxel = 4;
      break;

    case ARRAY_NRRD_TYPES["Float64Array"]:
      bytesPerVoxel = 8;
      break;

    default:
      throw new Error(`Unsupported Array Type ${configuration.arrayType}`);
  }

  // Buffer size is multiplied by bytesPerVoxel to allocate enough space.
  brushStackState.labelmaps3D[labelmapIndex] = {
    buffer: new ArrayBuffer(size * bytesPerVoxel),
    labelmaps2D: [],
    metadata: [],
    activeSegmentIndex: 1,
    colorLUTIndex: 0,
    segmentsHidden: [],
    undo: [],
    redo: [],
  };
}

/**
 * Adds a `Labelmap2D` view of one frame of a `Labelmap3D`.
 *
 * @param  {BrushStackState} brushStackState     The `BrushStackState` for a particular `Series`.
 * @param  {number} labelmapIndex       The labelmap index.
 * @param  {number} imageIdIndex        The stack position of the image.
 * @param  {number} rows                The number of rows in the image.
 * @param  {number} columns             The number of columns in the image.
 * @returns {null}
 */
export default function addLabelmap2D(
  brushStackState,
  labelmapIndex,
  imageIdIndex,
  rows,
  columns,
  elementOrEnabledElementUID
) {
  const { configuration } = segModule;

  const element = _getElement(elementOrEnabledElementUID);
  const stackState = csTools.getToolState(element, "stack");
  const numberOfFrames = stackState.data[0].imageIds.length;
  const slicelengthInBytes =
    brushStackState.labelmaps3D[labelmapIndex].buffer.byteLength /
    numberOfFrames;
  let pixelData;
  switch (configuration.arrayType) {
    case ARRAY_NRRD_TYPES["Int8Array"]:
      pixelData = new Int8Array(
        brushStackState.labelmaps3D[labelmapIndex].buffer,
        slicelengthInBytes * imageIdIndex, // 2 bytes/voxel
        slicelengthInBytes / 1
      );
      break;

    case ARRAY_NRRD_TYPES["Uint8Array"]:
      pixelData = new Uint8Array(
        brushStackState.labelmaps3D[labelmapIndex].buffer,
        slicelengthInBytes * imageIdIndex, // 2 bytes/voxel
        slicelengthInBytes / 1
      );
      break;

    case ARRAY_NRRD_TYPES["Int16Array"]:
      pixelData = new Int16Array(
        brushStackState.labelmaps3D[labelmapIndex].buffer,
        slicelengthInBytes * imageIdIndex, // 2 bytes/voxel
        slicelengthInBytes / 2
      );
      break;

    case ARRAY_NRRD_TYPES["Uint16Array"]:
      pixelData = new Uint16Array(
        brushStackState.labelmaps3D[labelmapIndex].buffer,
        slicelengthInBytes * imageIdIndex, // 2 bytes/voxel
        slicelengthInBytes / 2
      );
      break;

    case ARRAY_NRRD_TYPES["Int32Array"]:
      pixelData = new Int32Array(
        brushStackState.labelmaps3D[labelmapIndex].buffer,
        slicelengthInBytes * imageIdIndex,
        slicelengthInBytes / 4
      );
      break;

    case ARRAY_NRRD_TYPES["Float32Array"]:
      pixelData = new Float32Array(
        brushStackState.labelmaps3D[labelmapIndex].buffer,
        slicelengthInBytes * imageIdIndex,
        slicelengthInBytes / 4
      );
      break;

    case ARRAY_NRRD_TYPES["Float64Array"]:
      pixelData = new Float64Array(
        brushStackState.labelmaps3D[labelmapIndex].buffer,
        slicelengthInBytes * imageIdIndex,
        slicelengthInBytes / 8
      );
      break;

    default:
      throw new Error(`Unsupported Array Type ${configuration.arrayType}`);
  }

  brushStackState.labelmaps3D[labelmapIndex].labelmaps2D[imageIdIndex] = {
    pixelData,
    segmentsOnLabelmap: [],
  };
}

/**
 * Returns the cornerstone enabled element given either the element or its enabledElement UUID.
 *
 * @param  {string|HTMLElement} elementOrEnabledElementUID  The enabledElement
 *                                                          or its UUID.
 * @returns {HTMLElement}
 */
function _getElement(elementOrEnabledElementUID) {
  if (elementOrEnabledElementUID instanceof HTMLElement) {
    return elementOrEnabledElementUID;
  }

  return getters.enabledElementByUID(elementOrEnabledElementUID);
}

export { getLabelmap2D, addLabelmap2D, addLabelmap3D };
